import React, { useState, useRef, useEffect } from 'react';
import { Info, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Tooltip = ({ content }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const tooltipRef = useRef(null);
  const buttonRef = useRef(null);
  const { t } = useTranslation();

  const updateTooltipPosition = () => {
    if (!isOpen || !tooltipRef.current || !buttonRef.current) return;
    
    const viewportWidth = window.innerWidth;
    const buttonRect = buttonRef.current.getBoundingClientRect();
    
    if (viewportWidth < 768) {
      setTooltipStyle({
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 'calc(100vw - 32px)',
        maxWidth: '420px',
        maxHeight: '80vh',
        zIndex: 9999
      });
    } else {
      const isRightOverflow = buttonRect.right + 320 > viewportWidth; 
      
      const horizontalPosition = isRightOverflow
        ? { right: '0' }
        : { left: '0' };
      
      setTooltipStyle({
        position: 'absolute',
        top: '2rem',
        ...horizontalPosition,
        width: '20rem',
        maxHeight: '60vh'
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updateTooltipPosition();
      window.addEventListener('resize', updateTooltipPosition);
      
      if (window.innerWidth < 768) {
        document.body.style.overflow = 'hidden';
      }
      
      return () => {
        window.removeEventListener('resize', updateTooltipPosition);
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="p-1.5 text-blue-600 hover:text-blue-700 rounded-full hover:bg-blue-50 transition-colors"
      >
        <Info className="w-5 h-5" />
      </button>

      {isOpen && (
        <>
          <div 
            ref={tooltipRef}
            style={tooltipStyle}
            className="bg-white border border-gray-200 rounded-lg shadow-lg z-50 tooltip overflow-hidden flex flex-col"
          >
            <div className="flex justify-between items-center p-3 bg-blue-50 border-b border-gray-200">
              <h3 className="font-semibold text-blue-600">{t("tooltip.title")}</h3>
              <button 
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <div className="p-4 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed overflow-y-auto flex-1">
              {content}
            </div>
          </div>
          
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setIsOpen(false)}
          />
        </>
      )}
    </div>
  );
};

export default Tooltip;